/**
 * AssessmentSelectionPage - Página de seleção de tipo de assessment
 * @module features/assessment-selection/AssessmentSelectionPage
 *
 * Permite ao usuário escolher entre dois tipos de assessment:
 * - Governança de Produtos: Avaliação de gestão para líderes de produto
 * - Performance da Operação Tech: Avaliação técnica para líderes de tecnologia
 *
 * US-020: Redesign da Página Assessment Selection
 */

import { useSessionStore } from '@/features/chat/stores/sessionStore';
import type { AssessmentType } from '@/features/chat/types';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AssessmentTypeCard } from './components/AssessmentTypeCard';

// =============================================================================
// CONSTANTS
// =============================================================================

const assessmentTypes = [
  {
    type: 'governance' as const,
    title: 'Governança de Produtos',
    promise: 'Descubra se sua organização está preparada para escalar produtos com consistência.',
    evaluationPoints: [
      'Clareza de visão e priorização',
      'Alinhamento entre times e stakeholders',
      'Processos de discovery e delivery',
    ] as [string, string, string],
    indicatedFor: 'Indicado para líderes de Produto, Growth e Negócio',
    transitionDelay: '100ms',
  },
  {
    type: 'tech' as const,
    title: 'Performance da Operação Tech',
    promise:
      'Identifique gargalos que impedem sua engenharia de entregar com velocidade e qualidade.',
    evaluationPoints: [
      'Eficiência do ciclo de desenvolvimento',
      'Qualidade técnica e débito acumulado',
      'Capacidade de resposta a mudanças',
    ] as [string, string, string],
    indicatedFor: 'Indicado para líderes de Tecnologia e Engenharia',
    transitionDelay: '200ms',
  },
] as const;

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Página de seleção de tipo de assessment
 *
 * Exibe dois cards para o usuário escolher o tipo de avaliação
 * que deseja realizar antes de iniciar o chat.
 */
export function AssessmentSelectionPage() {
  const navigate = useNavigate();
  const setAssessmentType = useSessionStore((state) => state.setAssessmentType);
  const hasHydrated = useSessionStore((state) => state._hasHydrated);
  const hasRequiredFields = useSessionStore((state) => state.hasAllRequiredFields());
  const [selectedType, setSelectedType] = useState<AssessmentType | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  // Track mount to prevent animation restart on Zustand hydration re-render
  const [hasMounted, setHasMounted] = useState(false);

  // Route guard: redirect to home if required fields not filled (FIX-029)
  useEffect(() => {
    if (hasHydrated && !hasRequiredFields) {
      navigate('/', { replace: true });
    }
  }, [hasHydrated, hasRequiredFields, navigate]);

  useEffect(() => {
    // Small delay to ensure animation plays after hydration
    const timer = setTimeout(() => setHasMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  // Don't render content until store is hydrated
  if (!hasHydrated) return null;

  /**
   * Handler para seleção de tipo de assessment
   * Persiste o tipo no store e navega para o chat
   */
  const handleSelect = useCallback(
    (type: AssessmentType) => {
      setSelectedType(type);
      setIsTransitioning(true);

      // Persiste o tipo selecionado
      setAssessmentType(type);

      // Aguarda animação de transição antes de navegar
      setTimeout(() => {
        navigate('/assessment/chat');
      }, 300);
    },
    [setAssessmentType, navigate]
  );

  return (
    <main className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 sm:py-12 lg:py-16">
        {/* Link de voltar */}
        <Link
          to="/"
          className={`inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 fade-enter ${
            hasMounted ? 'is-visible' : ''
          }`}
          aria-label="Voltar para a página inicial"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Voltar</span>
        </Link>

        {/* Header */}
        <header
          className={`text-center mb-8 sm:mb-12 fade-enter ${hasMounted ? 'is-visible' : ''}`}
          style={{ transitionDelay: '50ms' }}
        >
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-semibold text-foreground mb-2">
            Escolha o tipo de review
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Escolha o diagnóstico mais relevante para o seu momento e vá direto ao ponto.
          </p>
        </header>

        {/* Cards de seleção */}
        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto"
          data-testid="assessment-selection-grid"
        >
          {assessmentTypes.map(
            ({ type, title, promise, evaluationPoints, indicatedFor, transitionDelay }) => (
              <AssessmentTypeCard
                key={type}
                type={type}
                title={title}
                promise={promise}
                evaluationPoints={evaluationPoints}
                indicatedFor={indicatedFor}
                onSelect={handleSelect}
                isSelected={selectedType === type}
                isLoading={isTransitioning && selectedType === type}
                shouldAnimate={hasMounted}
                style={{ transitionDelay }}
              />
            )
          )}
        </div>
      </div>
    </main>
  );
}

export default AssessmentSelectionPage;
